import { useMemo, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import PrintIcon from "@mui/icons-material/Print";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { QRCodeSVG } from "qrcode.react";

import lottoRecoIcon from "../../../assets/images/lottoReco-icon.png";
import { AuditCodeListItem } from "../../../services/auditCodeService";
import { useAuditCodes } from "../hooks/queries/auditCodeQueries";
import { useGenerateAuditCodeMutation, useRevokeAuditCodeMutation } from "../hooks/mutations/auditCodeMutations";
import { buildAuditCodeAccessUrl } from "../utils/auditCodeUrls";

type Props = {
    storeId: string;
};

const buildLiveAuditStatusUrl = (storeId: string) => {
    const basePath = process.env.PUBLIC_URL || "/app";
    return `${window.location.origin}${basePath}/store/${storeId}/audit-live-status`;
};

const formatLocationType = (value: string) => value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatCreatedAt = (value: string | null) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
};

const getMaskedCode = (code: string | null) => {
    if (!code) {
        return null;
    }

    return `Code: ****${code.slice(-8).toUpperCase()}`;
};

export default function AuditCodeManagement({ storeId }: Props) {
    const { data: auditCodes = [], isLoading } = useAuditCodes(storeId);
    const generateMutation = useGenerateAuditCodeMutation();
    const revokeMutation = useRevokeAuditCodeMutation();
    const [selectedLocation, setSelectedLocation] = useState<AuditCodeListItem | null>(null);
    const [locationToRotate, setLocationToRotate] = useState<AuditCodeListItem | null>(null);
    const [liveAuditQrOpen, setLiveAuditQrOpen] = useState(false);

    const liveAuditStatusUrl = useMemo(() => buildLiveAuditStatusUrl(storeId), [storeId]);

    const selectedAccessUrl = useMemo(() => {
        if (!selectedLocation?.code) {
            return "";
        }
        return buildAuditCodeAccessUrl(selectedLocation.code);
    }, [selectedLocation]);

    const handleGenerate = async (location: AuditCodeListItem) => {
        const response = await generateMutation.mutateAsync({
            storeId,
            storeLocationId: location.store_location_id,
        });
        setSelectedLocation({ ...location, code: response.code, created_at: response.created_at });
        setLocationToRotate(null);
    };

    const handleRevoke = async (location: AuditCodeListItem) => {
        await revokeMutation.mutateAsync({
            storeId,
            storeLocationId: location.store_location_id,
        });
        if (selectedLocation?.store_location_id === location.store_location_id) {
            setSelectedLocation(null);
        }
    };

    return (
        <Box
            sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                backgroundColor: "#FFFFFF",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                mb: 2,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    borderBottom: "1px solid #E2E8F0",
                    display: "flex",
                    gap: 1.25,
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                }}
            >
                <QrCode2Icon sx={{ color: "#2563EB" }} />
                <Box>
                    <Typography sx={{ color: "#1F2A44", fontSize: 16, fontWeight: 850, lineHeight: 1.25 }}>
                        LottoReco Audit Codes
                    </Typography>
                    <Typography sx={{ color: "#64748B", fontSize: 12.5, fontWeight: 600, mt: 0.25 }}>
                        Generate one active scan code per store location.
                    </Typography>
                </Box>
            </Box>

            {isLoading ? (
                <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : auditCodes.length === 0 ? (
                <Box sx={{ px: 2, py: 3 }}>
                    <Typography sx={{ color: "#64748B", fontSize: 13, fontWeight: 700 }}>
                        No active store locations are available.
                    </Typography>
                </Box>
            ) : (
                <Stack divider={<Box sx={{ borderTop: "1px solid #EEF2F7" }} />}>
                    {auditCodes.map((location) => {
                        const hasCode = Boolean(location.code);
                        const isBusy = generateMutation.isPending || revokeMutation.isPending;
                        const createdAtLabel = formatCreatedAt(location.created_at);
                        const maskedCode = getMaskedCode(location.code);
                        const codeMetadata = hasCode
                            ? [
                                "Code Active",
                                createdAtLabel ? `Created ${createdAtLabel}` : null,
                                maskedCode,
                            ].filter(Boolean).join(" · ")
                            : "No code";
                        return (
                            <Box
                                key={location.store_location_id}
                                sx={{
                                    alignItems: { xs: "flex-start", md: "center" },
                                    display: "grid",
                                    gap: 1.25,
                                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
                                    px: { xs: 1.5, sm: 2 },
                                    py: 1.5,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ color: "#1E293B", fontSize: 14, fontWeight: 850, lineHeight: 1.3 }}>
                                        {location.store_location_name}
                                    </Typography>
                                    <Typography sx={{ color: "#64748B", fontSize: 12.5, fontWeight: 650, mt: 0.25 }}>
                                        {formatLocationType(location.store_location_type)} · {codeMetadata}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    <Button
                                        size="small"
                                        variant={hasCode ? "outlined" : "contained"}
                                        color={hasCode ? "warning" : "primary"}
                                        startIcon={<RefreshIcon />}
                                        disabled={isBusy || !location.active}
                                        onClick={() => {
                                            if (hasCode) {
                                                setLocationToRotate(location);
                                                return;
                                            }
                                            handleGenerate(location);
                                        }}
                                        sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}
                                    >
                                        {hasCode ? "Rotate Code" : "Generate Code"}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<VisibilityIcon />}
                                        disabled={!hasCode}
                                        onClick={() => setSelectedLocation(location)}
                                        sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}
                                    >
                                        View
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<PrintIcon />}
                                        disabled={!hasCode}
                                        onClick={() => {
                                            setSelectedLocation(location);
                                            window.setTimeout(() => window.print(), 150);
                                        }}
                                        sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}
                                    >
                                        Print
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        startIcon={<DeleteOutlineIcon />}
                                        disabled={!hasCode || isBusy}
                                        onClick={() => handleRevoke(location)}
                                        sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}
                                    >
                                        Revoke
                                    </Button>
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            <Box
                sx={{
                    borderTop: "1px solid #E2E8F0",
                    display: "grid",
                    gap: 1.25,
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ alignItems: "center", display: "flex", gap: 0.75 }}>
                        <LiveTvIcon sx={{ color: "#0F766E", fontSize: 20 }} />
                        <Typography sx={{ color: "#1E293B", fontSize: 14, fontWeight: 850, lineHeight: 1.3 }}>
                            Live Audit Review
                        </Typography>
                    </Box>
                    <Typography sx={{ color: "#64748B", fontSize: 12.5, fontWeight: 650, mt: 0.25 }}>
                        Store-level QR for reviewing LottoReco COM port scanner activity while an audit is in progress.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setLiveAuditQrOpen(true)}
                        sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}
                    >
                        View
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={() => {
                            setLiveAuditQrOpen(true);
                            window.setTimeout(() => window.print(), 150);
                        }}
                        sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}
                    >
                        Print
                    </Button>
                </Box>
            </Box>

            <Dialog open={liveAuditQrOpen} onClose={() => setLiveAuditQrOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                    <Typography sx={{ color: "#1F2A44", fontSize: 18, fontWeight: 850 }}>
                        Live Audit Review
                    </Typography>
                    <IconButton aria-label="Close" onClick={() => setLiveAuditQrOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.75} alignItems="center">
                        <QRCodeSVG
                            value={liveAuditStatusUrl}
                            size={232}
                            level="H"
                            marginSize={2}
                            imageSettings={{
                                src: lottoRecoIcon,
                                height: 42,
                                width: 42,
                                excavate: true,
                            }}
                        />
                        <Typography sx={{ color: "#1E293B", fontSize: 14, fontWeight: 850, textAlign: "center" }}>
                            Live Audit Review
                        </Typography>
                        <Typography sx={{ color: "#64748B", fontSize: 12.5, fontWeight: 650, textAlign: "center" }}>
                            Opens the in-progress scan review screen for the LottoReco COM port scanner.
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, "@media print": { display: "none" } }}>
                    <Button startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ fontWeight: 800, textTransform: "none" }}>
                        Print
                    </Button>
                    <Button variant="contained" onClick={() => setLiveAuditQrOpen(false)} sx={{ fontWeight: 800, textTransform: "none" }}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(selectedLocation?.code)} onClose={() => setSelectedLocation(null)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ position: "relative", px: 6, textAlign: "center" }}>
                    <Typography sx={{ color: "#1F2A44", fontSize: 18, fontWeight: 850, textAlign: "center" }}>
                        LottoReco Audit Code
                    </Typography>
                    <IconButton
                        aria-label="Close"
                        onClick={() => setSelectedLocation(null)}
                        sx={{ position: "absolute", right: 12, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.75} alignItems="center">
                        {selectedAccessUrl && (
                            <QRCodeSVG
                                value={selectedAccessUrl}
                                size={232}
                                level="H"
                                marginSize={2}
                                imageSettings={{
                                    src: lottoRecoIcon,
                                    height: 42,
                                    width: 42,
                                    excavate: true,
                                }}
                            />
                        )}
                        {getMaskedCode(selectedLocation?.code || null) && (
                            <Typography
                                sx={{
                                    color: "#1E293B",
                                    fontFamily: "monospace",
                                    fontSize: 15,
                                    fontWeight: 850,
                                    letterSpacing: 0,
                                    textAlign: "center",
                                }}
                            >
                                {getMaskedCode(selectedLocation?.code || null)}
                            </Typography>
                        )}
                        <Typography sx={{ color: "#1E293B", fontSize: 14, fontWeight: 850, textAlign: "center" }}>
                            {selectedLocation?.store_location_name}
                        </Typography>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, "@media print": { display: "none" } }}>
                    <Button startIcon={<PrintIcon />} onClick={() => window.print()} sx={{ fontWeight: 800, textTransform: "none" }}>
                        Print
                    </Button>
                    <Button variant="contained" onClick={() => setSelectedLocation(null)} sx={{ fontWeight: 800, textTransform: "none" }}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(locationToRotate)} onClose={() => setLocationToRotate(null)} fullWidth maxWidth="xs">
                <DialogTitle>
                    <Typography sx={{ color: "#1F2A44", fontSize: 18, fontWeight: 850 }}>
                        Rotate LottoReco Audit Code?
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#475569", fontSize: 14, fontWeight: 650, lineHeight: 1.5 }}>
                        This will replace the current QR code for {locationToRotate?.store_location_name}. Any printed or currently displayed QR code for this location will stop working.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        onClick={() => setLocationToRotate(null)}
                        disabled={generateMutation.isPending}
                        sx={{ fontWeight: 800, textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        disabled={!locationToRotate || generateMutation.isPending}
                        onClick={() => {
                            if (locationToRotate) {
                                handleGenerate(locationToRotate);
                            }
                        }}
                        sx={{ fontWeight: 800, textTransform: "none" }}
                    >
                        {generateMutation.isPending ? "Rotating..." : "Rotate Code"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
